import { differenceInHours, differenceInMilliseconds } from 'date-fns';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { useCallback, useEffect, useState } from 'react';

import {
  ACCOUNT_PORTAL_ROOT,
  oryLogout,
  PREVIEW_TRIAL_COOKIE_NAME,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useGetLoggedInUser,
  useImpersonationSelector,
  useUserTrialDetails,
} from '@revelio/auth';
import { getCookie, userTracking } from '@revelio/core';
import type { KeyValueResp } from '@revelio/data-access';

type TrialAlertState = {
  showTrialChip: boolean;
  days: number;
  hours: number;
};

const defaultTrialAlertState: TrialAlertState = {
  showTrialChip: false,
  days: 0,
  hours: 0,
};

let cachedTrialAlertState: TrialAlertState | undefined;

export const useFreeTrialAlertModalState = () => {
  const {
    loggedInUser,
    query: [{ fetching }],
  } = useGetLoggedInUser();

  const [trialAlertState, setTrialAlertState] = useState<TrialAlertState>(
    cachedTrialAlertState ?? defaultTrialAlertState
  );
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);
  const shouldBypassLogout = isImpersonating || Boolean(overrides);

  const showTrialAlert = useCallback(
    (expDate: Date) => {
      const hoursLeft = differenceInHours(expDate, new Date(), {
        roundingMethod: 'ceil',
      });

      const daysLeft = Math.ceil(hoursLeft / 24);

      if (hoursLeft < 1) {
        if (shouldBypassLogout) {
          const newState: TrialAlertState = {
            showTrialChip: true,
            days: 0,
            hours: 0,
          };
          cachedTrialAlertState = newState;
          setTrialAlertState(newState);
          return;
        }

        userTracking.reset();
        const loggedInUserName = loggedInUser.name;
        const loggedInUserEmail = loggedInUser.email;
        oryLogout().then(() => {
          window.location.assign(
            `${ACCOUNT_PORTAL_ROOT}/trial-expired?name=${loggedInUserName}&email=${loggedInUserEmail}`
          );
        });
      } else {
        const newState: TrialAlertState = {
          showTrialChip: true,
          days: daysLeft,
          hours: hoursLeft,
        };

        cachedTrialAlertState = newState;
        setTrialAlertState(newState);
      }
    },
    [loggedInUser, shouldBypassLogout]
  );

  const { isTrialUser } = useUserTrialDetails();

  useEffect(() => {
    if (fetching || isEmpty(loggedInUser)) {
      return;
    }

    const userMetaData = get(loggedInUser, 'metadata', []);
    const userTrialExpirationDataMeta = find(userMetaData, {
      key: 'expiration_date',
    });
    const trialExpirationDate =
      (userTrialExpirationDataMeta as KeyValueResp)?.value ||
      getCookie(PREVIEW_TRIAL_COOKIE_NAME); // format e.g. 2024-03-10T00:09:19Z
    let timeout: NodeJS.Timeout;
    let timer: NodeJS.Timer;

    if (!isTrialUser || isUndefined(trialExpirationDate)) {
      cachedTrialAlertState = defaultTrialAlertState;
      setTrialAlertState(defaultTrialAlertState);
      return;
    }

    if (isTrialUser && !isUndefined(trialExpirationDate)) {
      const expDate = new Date(trialExpirationDate);
      showTrialAlert(expDate);
      if (expDate.getMinutes() === 0) {
        timer = setInterval(
          () => {
            showTrialAlert(expDate);
          },
          1000 * 60 * 60
        );
      } else {
        const millisecondsLeft = differenceInMilliseconds(expDate, new Date());
        const millisecondsLeftToHour = millisecondsLeft % (1000 * 60 * 60);
        timeout = setTimeout(() => {
          showTrialAlert(expDate);
          timer = setInterval(
            () => {
              showTrialAlert(expDate);
            },
            1000 * 60 * 60
          );
        }, millisecondsLeftToHour);
      }
    }

    return () => {
      clearTimeout(timeout);
      clearInterval(timer);
    };
  }, [fetching, loggedInUser, showTrialAlert, isTrialUser]);

  return trialAlertState;
};
